import { Download, X } from 'lucide-react';
import type { PairFilters } from '../../types/customerDuplicates';
import { ALL_STATUSES, STATUS_LABELS, ALL_COMPANIES, COMPANY_LABELS, ALL_MARKERS, MARKER_LABELS, RETIRED_MARKERS } from '../../types/customerDuplicates';

interface Props {
    filters: PairFilters;
    activeFilterCount: number;
    onChange: (next: Partial<PairFilters>) => void;
    onClear: () => void;
    onExport: () => void;
    exporting: boolean;
}

export function FiltersBar({ filters, activeFilterCount, onChange, onClear, onExport, exporting }: Props) {
    return (
        <div className="flex flex-wrap items-end gap-3">
            <label className="block">
                <span className="mb-1 block text-xs font-medium text-gray-600">Search</span>
                <input
                    value={filters.search}
                    onChange={e => onChange({ search: e.target.value })}
                    placeholder="Customer name, code or ID"
                    className="h-9 w-64 rounded border border-gray-300 px-2 text-sm"
                />
            </label>

            <label className="block">
                <span className="mb-1 block text-xs font-medium text-gray-600">Company</span>
                <select
                    value={filters.company}
                    onChange={e => onChange({ company: e.target.value })}
                    className="h-9 w-44 rounded border border-gray-300 px-2 text-sm"
                >
                    <option value="">All companies</option>
                    {ALL_COMPANIES.map(c => (
                        <option key={c} value={c}>{COMPANY_LABELS[c]}</option>
                    ))}
                </select>
            </label>

            <label className="block">
                <span className="mb-1 block text-xs font-medium text-gray-600">Level</span>
                <select
                    value={filters.level}
                    onChange={e => onChange({ level: e.target.value })}
                    className="h-9 w-40 rounded border border-gray-300 px-2 text-sm"
                >
                    <option value="">Any level</option>
                    <option value="1">Level 1 — same legal entity</option>
                    <option value="2">Level 2 — same firm/group</option>
                </select>
            </label>

            <label className="block">
                <span className="mb-1 block text-xs font-medium text-gray-600">Scope</span>
                <select
                    value={filters.scope}
                    onChange={e => onChange({ scope: e.target.value })}
                    className="h-9 w-40 rounded border border-gray-300 px-2 text-sm"
                >
                    <option value="">Any scope</option>
                    <option value="INTRA">Within one company</option>
                    <option value="CROSS">Across companies</option>
                </select>
            </label>

            <label className="block">
                <span className="mb-1 block text-xs font-medium text-gray-600">Status</span>
                <select
                    value={filters.status}
                    onChange={e => onChange({ status: e.target.value })}
                    className="h-9 w-44 rounded border border-gray-300 px-2 text-sm"
                >
                    <option value="ALL">All statuses</option>
                    {ALL_STATUSES.map(s => (
                        <option key={s} value={s}>{STATUS_LABELS[s]}</option>
                    ))}
                </select>
            </label>

            <label className="block">
                <span className="mb-1 block text-xs font-medium text-gray-600">Secondary marker</span>
                <select
                    value={filters.marker}
                    onChange={e => onChange({ marker: e.target.value })}
                    className="h-9 w-64 rounded border border-gray-300 px-2 text-sm"
                >
                    <option value="">Any marker</option>
                    {ALL_MARKERS.filter(m => !RETIRED_MARKERS.includes(m) || m === filters.marker).map(m => (
                        <option key={m} value={m}>{MARKER_LABELS[m]}</option>
                    ))}
                </select>
            </label>

            <label className="block">
                <span className="mb-1 block text-xs font-medium text-gray-600">Still found</span>
                <select
                    value={filters.presence}
                    onChange={e => onChange({ presence: e.target.value })}
                    className="h-9 w-52 rounded border border-gray-300 px-2 text-sm"
                    title={
                        'A pair the latest run no longer finds is kept and flagged, never deleted - '
                        + 'usually because a field changed in the ERP. "No longer found" is how you reach those.'
                    }
                >
                    <option value="CURRENT">Found by the last run</option>
                    <option value="GONE">No longer found</option>
                    <option value="ALL">Both</option>
                </select>
            </label>

            <label className="flex h-9 items-center gap-2 text-sm text-gray-700">
                <input
                    type="checkbox"
                    checked={filters.onlyNew}
                    onChange={e => onChange({ onlyNew: e.target.checked })}
                />
                Only new since the last run
            </label>

            {activeFilterCount > 0 && (
                <button
                    onClick={onClear}
                    className="inline-flex h-9 items-center gap-1 rounded border border-gray-300 px-3 text-sm text-gray-600 hover:bg-gray-50"
                >
                    <X className="h-4 w-4" /> Clear filters ({activeFilterCount})
                </button>
            )}

            <button
                onClick={onExport}
                disabled={exporting}
                className="ml-auto inline-flex h-9 items-center gap-2 rounded border border-gray-300 px-3 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                title="Excel export of exactly the currently filtered set"
            >
                <Download className="h-4 w-4" /> {exporting ? 'Exporting…' : 'Export'}
            </button>
        </div>
    );
}
